import dayjs from "lib/dayjs";
import { buildDebtServiceSchedule, normalizeTermCoupons } from "./buildDebtServiceSchedule";
import { solveWithGLPK } from "lib/glpkAdapter";
import { normalizeRefundingSolutionType } from "./refundingSolutionTypes";

const num = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;
const frequencyMonths = (value) => ({ monthly: 1, quarterly: 3, semiannual: 6, annual: 12 }[String(value || "semiannual").toLowerCase()] || 6);

export function buildPriorDebtServiceCashflows(refundedIssues = [], settlementDate) {
  const settlement = dayjs(settlementDate).startOf("day");
  const totals = new Map();
  for (const issue of refundedIssues || []) {
    const months = frequencyMonths(issue?.coupon_frequency);
    for (const maturity of issue?.selected_maturities || []) {
      const end = dayjs(maturity?.maturity_date).startOf("day");
      if (!end.isValid()) continue;
      const principal = num(maturity?.principal);
      const coupon = num(maturity?.coupon) / (num(maturity?.coupon) > 1 ? 100 : 1);
      let date = end;
      while (date.isAfter(settlement, "day")) {
        const key = date.format("YYYY-MM-DD");
        totals.set(key, num(totals.get(key)) + principal * coupon * months / 12);
        date = date.subtract(months, "month");
      }
      const key = end.format("YYYY-MM-DD");
      totals.set(key, num(totals.get(key)) + principal);
    }
  }
  return [...totals.entries()].map(([date, total]) => ({ date, total })).sort((a, b) => a.date.localeCompare(b.date));
}

export function bucketCashflowsByPeriod(cashflows, periodEnds, startDate) {
  let start = dayjs(startDate).startOf("day");
  return (periodEnds || []).map((endValue) => {
    const end = dayjs(endValue).startOf("day");
    const total = (cashflows || []).reduce((sum, row) => {
      const date = dayjs(row.date).startOf("day");
      return date.isAfter(start, "day") && !date.isAfter(end, "day") ? sum + num(row.total) : sum;
    }, 0);
    start = end;
    return Math.round(total * 100) / 100;
  });
}

function debtServiceColumns({ rows, form, denomination }) {
  const ends = rows.map((row) => row.date);
  return rows.map((_, index) => {
    const unitRows = rows.map((row, i) => ({ ...row, principal: i === index ? denomination : 0 }));
    const schedule = buildDebtServiceSchedule({ rows: unitRows, form }) || [];
    return bucketCashflowsByPeriod(schedule.map((r) => ({ date: r.date, total: num(r.netDebtService ?? r.total) })), ends, form.deliveryDate || form.datedDate);
  });
}

export async function solveRefundingStructure({ rows, form, requiredPar, priorDebtService, solutionType = "uniform", fundingTarget, costOfIssuance, additionalSources = 0 }) {
  const denomination = Math.max(0.01, num(form?.denomination) || 5000);
  let totalUnits = Math.ceil(num(requiredPar) / denomination);
  const columns = debtServiceColumns({ rows, form, denomination });
  const pricedRows = normalizeTermCoupons(rows);
  const proceedsPerUnit = pricedRows.map((row) => denomination * num(row?.price ?? 100) / 100);
  const n = rows.length;
  if (!n) throw new Error("Add refunding maturity rows before solving.");
  if (!Array.isArray(priorDebtService) || priorDebtService.length !== n) {
    throw new Error("The prior debt-service periods do not match the refunding maturity rows.");
  }
  if (form?.finalMaturityDate && rows.some((row) => dayjs(row.date).isAfter(form.finalMaturityDate, "day"))) {
    throw new Error("Refunding maturity rows extend beyond the deal's final maturity date. Update the final maturity date before solving.");
  }
  const maxUnitProceeds = Math.max(...proceedsPerUnit);
  let fundingFeasibleAtSomePar = false;
  let levelModelFeasible = false;
  if (fundingTarget != null && maxUnitProceeds <= 0 && num(fundingTarget) > num(additionalSources)) {
    throw new Error("The refunding deposit cannot be funded because the maturity rows have no positive bond proceeds.");
  }
  // Skip par amounts that cannot fund the deposit even at the best available price.
  // Re-evaluate COI because it may be expressed as a percentage of par.
  if (fundingTarget != null && maxUnitProceeds > 0) {
    for (let i = 0; i < 20; i++) {
      const par = totalUnits * denomination;
      const coi = typeof costOfIssuance === "function" ? num(costOfIssuance(par)) : num(costOfIssuance);
      const minimumUnits = Math.max(0, Math.ceil((num(fundingTarget) + coi - num(additionalSources) - 0.004) / maxUnitProceeds));
      if (minimumUnits <= totalUnits) break;
      totalUnits = minimumUnits;
    }
  }
  const savingsType = normalizeRefundingSolutionType(solutionType);
  const uniform = savingsType === "uniform";
  const N = n + (uniform ? 1 : 0);
  const c = Array(N).fill(0);
  if (uniform) {
    c[n] = -1; // Maximize a common annual savings amount, within denomination tolerance.
  } else {
    // Prior service is fixed, so minimizing weighted new service maximizes
    // weighted savings. Earlier periods receive more weight for upfront savings.
    for (let j = 0; j < n; j++) {
      c[j] = columns[j].reduce((sum, service, y) => {
        const weight = savingsType === "upfront" ? n - y : y + 1;
        return sum + weight * num(service);
      }, 0) / denomination;
    }
  }
  for (let attempt = 0; attempt < 500; attempt++, totalUnits++) {
    const parAmount = totalUnits * denomination;
    const coi = typeof costOfIssuance === "function" ? num(costOfIssuance(parAmount)) : num(costOfIssuance);
    const requiredGross = num(fundingTarget) + coi - num(additionalSources);
    if (fundingTarget != null && maxUnitProceeds * totalUnits + 0.004 < requiredGross) continue;
    fundingFeasibleAtSomePar = true;
    const modelRows = [{ coeffs: [...Array(n).fill(1), ...(uniform ? [0] : [])], sense: "=", rhs: totalUnits }];
    if (uniform) {
      for (let y = 0; y < n; y++) {
        const serviceCoefficients = columns.map((column) => num(column[y]) / 1e6);
        const prior = num(priorDebtService[y]) / 1e6;
        const tolerance = denomination / 1e6;
        modelRows.push({
          coeffs: [...serviceCoefficients, 1],
          sense: "<=",
          rhs: prior + tolerance,
        });
        modelRows.push({
          coeffs: [...serviceCoefficients.map((value) => -value), -1],
          sense: "<=",
          rhs: -prior + tolerance,
        });
      }
    }
    if (fundingTarget != null) {
      modelRows.push({ coeffs: [...proceedsPerUnit.map((value) => value / 1e6), ...(uniform ? [0] : [])], sense: ">=", rhs: (requiredGross - 0.004) / 1e6 });
    }
    const lb = Array(N).fill(0);
    const ub = Array(N).fill(totalUnits);
    if (uniform) {
      const maxPeriodService = Math.max(0, ...columns.flat());
      const savingsBound = Math.max(1, totalUnits * maxPeriodService / 1e6 +
        Math.max(0, ...priorDebtService.map((value) => Math.abs(num(value)) / 1e6)));
      lb[n] = -savingsBound;
      ub[n] = savingsBound;
    }
    const modelIsSatisfied = (candidate, constraints) =>
      Array.isArray(candidate?.x) && candidate.x.length === N &&
      candidate.x.every((value, index) => Number.isFinite(value) &&
        value >= lb[index] - 1e-6 && value <= ub[index] + 1e-6) &&
      constraints.every((row) => {
      const actual = row.coeffs.reduce((sum, coefficient, index) => sum + coefficient * num(candidate?.x?.[index]), 0);
      const tolerance = Math.max(1e-6, Math.abs(row.rhs) * 1e-8);
      if (row.sense === "=") return Math.abs(actual - row.rhs) <= tolerance;
      if (row.sense === "<=") return actual <= row.rhs + tolerance;
      return actual + tolerance >= row.rhs;
      });
    const solved = await solveWithGLPK({ c, rows: modelRows, lb, ub, N });
    if (!solved?.vars || !Object.keys(solved.vars).length || !modelIsSatisfied(solved, modelRows)) continue;
    levelModelFeasible = true;
    const raw = solved.x.slice(0, n);
    const units = raw.map((value) => Math.max(0, Math.floor(value + 1e-7)));
    let remaining = totalUnits - units.reduce((sum, value) => sum + value, 0);
    const available = new Set(rows.map((_, index) => index));
    while (remaining > 0 && available.size) {
      const currentGross = units.reduce((sum, value, j) => sum + value * proceedsPerUnit[j], 0);
      const eligible = [...available].filter((index) => {
        if (uniform && priorDebtService.some((prior, y) => {
          const used = units.reduce((sum, value, j) => sum + value * num(columns[j][y]), 0);
          return used + num(columns[index][y]) > num(prior) + denomination + 0.01;
        })) return false;
        if (fundingTarget == null) return true;
        const otherPrices = [...available].filter((j) => j !== index)
          .map((j) => proceedsPerUnit[j]).sort((a, b) => b - a);
        const maxGross = currentGross + proceedsPerUnit[index] +
          otherPrices.slice(0, remaining - 1).reduce((sum, price) => sum + price, 0);
        return maxGross + 0.004 >= requiredGross;
      });
      if (!eligible.length) break;
      eligible.sort((a, b) => uniform
        ? (raw[b] - Math.floor(raw[b])) - (raw[a] - Math.floor(raw[a]))
        : c[a] - c[b] || (raw[b] - Math.floor(raw[b])) - (raw[a] - Math.floor(raw[a])));
      const index = eligible[0];
      units[index] += 1;
      available.delete(index);
      remaining -= 1;
    }
    if (remaining > 0) continue;
    const grossProceeds = units.reduce((sum, value, j) => sum + value * proceedsPerUnit[j], 0);
    if (fundingTarget != null && grossProceeds + num(additionalSources) - coi + 0.004 < num(fundingTarget)) continue;
    const schedule = pricedRows.map((row, i) => ({ ...row, principal: units[i] * denomination }));
    const cashflows = buildDebtServiceSchedule({ rows: schedule, form }) || [];
    const newDebtService = bucketCashflowsByPeriod(
      cashflows.map((row) => ({ date: row.date, total: num(row.netDebtService ?? row.total) })),
      rows.map((row) => row.date), form.deliveryDate || form.datedDate
    );
    const savings = priorDebtService.map((old, i) => old - newDebtService[i]);
    if (uniform) {
      const spread = Math.max(...savings) - Math.min(...savings);
      const averageSavings = savings.reduce((sum, value) => sum + value, 0) / n;
      const allowedSpread = Math.max(2 * denomination, Math.abs(averageSavings) * 0.01);
      if (spread > allowedSpread + 0.01) continue;
    }
    return { schedule, parAmount, priorDebtService, newDebtService, savings, refundingSolutionType: savingsType };
  }
  throw new Error(uniform
    ? !fundingFeasibleAtSomePar
      ? "The refunding deposit cannot be funded within the par search range at the current bond prices. Review the scale, deposit, and costs of issuance."
      : !levelModelFeasible
        ? "No fully funded level-savings structure was found across all comparison periods. The funding test passed, but the level-savings constraints did not. Review maturities and coupons."
        : "A level-savings solution was found, but it could not be rounded to bond denominations while preserving full funding and level savings. Review denominations and pricing."
    : "The solver could not find a fully funded refunding structure within the par search range. Check deposit, bond prices, and costs of issuance.");
}
